import React, { useState, useRef, useEffect } from 'react';
import { AttachmentIcon, UploadIcon } from './icons';
import { ModalType, AttachedFile } from '../types';
import { AttachmentPreview } from './AttachmentPreview';
import { trackClick } from '../utils/analytics';
import { recordInterest } from '../utils/interest';

interface ChatInputProps {
  onSend: (prompt: string, files: File[]) => void;
  isLoading: boolean;
  openModal: (type: ModalType) => void;
  showConciergeLink?: boolean;
  hasMessages?: boolean; // To control placeholder visibility
  activeChatId?: string | null; // To trigger focus when chat changes
  disableStickyFocus?: boolean; // Disable auto-refocus behavior (useful for landing page)
  disableAttachments?: boolean; // Disable attachment functionality (useful for landing page)
}

export const ChatInput: React.FC<ChatInputProps> = ({ onSend, isLoading, openModal, showConciergeLink = true, hasMessages = false, activeChatId, disableStickyFocus = false, disableAttachments = false }) => {
  const [inputValue, setInputValue] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [inputValue]);

  // Auto-focus the input when component mounts, when chat changes, and maintain focus
  useEffect(() => {
    // Skip sticky focus behavior entirely if disabled (for landing page)
    if (disableStickyFocus) {
      return;
    }

    const focusInput = () => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    };

    // Focus on mount and when activeChatId changes
    focusInput();

    // Handle click events to maintain focus
    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Don't refocus if user clicked on another input, textarea, select, or contenteditable element
      if (target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.contentEditable === 'true' ||
        target.closest('input, textarea, select, [contenteditable="true"]')
      )) {
        return;
      }

      // Don't refocus if clicking on buttons, links, or interactive elements
      if (target && (
        target.tagName === 'BUTTON' ||
        target.tagName === 'A' ||
        target.closest('button, a, [role="button"]')
      )) {
        return;
      }

      // Don't refocus if user is interacting with a modal
      if (target && target.closest('[role="dialog"], .fixed.inset-0')) {
        return;
      }

      // Don't refocus if interacting with SVG elements (charts)
      if (target && (
        target.tagName === 'svg' ||
        target.tagName === 'path' ||
        target.tagName === 'circle' ||
        target.tagName === 'text' ||
        target.closest('svg')
      )) {
        return;
      }

      // Refocus the chat input after a short delay
      setTimeout(() => {
        if (textareaRef.current && document.activeElement !== textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 50);
    };

    document.addEventListener('click', handleDocumentClick);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [activeChatId, disableStickyFocus]); // Added activeChatId and disableStickyFocus dependencies

  const handleAttachmentClick = () => {
    trackClick('cosmos_click_attach');
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const fileWrappers: AttachedFile[] = newFiles.map(file => {
        const id = `${file.name}-${file.lastModified}-${Math.random()}`;
        if (file.size > 5 * 1024 * 1024) { // 5MB limit
          return { file, id, status: 'error', error: 'File size must be under 5MB' };
        }
        
        const wrapper: AttachedFile = { file, id, status: 'success' };
        
        if (file.type.startsWith('image/')) {
          wrapper.status = 'loading';
          const reader = new FileReader();
          reader.onloadend = () => {
            setAttachedFiles(prev => prev.map(f => f.id === id ? { ...f, previewUrl: reader.result as string, status: 'success' } : f));
          };
          reader.readAsDataURL(file);
        } else if (file.type === 'application/pdf') {
          wrapper.status = 'success'; // PDFs don't need preview processing here
        }
        return wrapper;
      });

      setAttachedFiles(prev => {
          const updatedFiles = [...prev];
          fileWrappers.forEach(fw => {
              if(!updatedFiles.find(f => f.file.name === fw.file.name && f.file.lastModified === fw.file.lastModified)) {
                  updatedFiles.push(fw);
              }
          });
          return updatedFiles;
      });
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleRemoveFile = (idToRemove: string) => {
    setAttachedFiles(prev => prev.filter(f => f.id !== idToRemove));
  };


  const handleSend = () => {
    const filesToSend = attachedFiles.filter(f => f.status === 'success').map(f => f.file);
    if (inputValue.trim() && !isLoading) {
      trackClick('cosmos_click_send');
      onSend(inputValue, filesToSend);
      setInputValue('');
      setAttachedFiles([]);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col items-center gap-2 sm:gap-4 self-stretch pb-4 sm:pb-6 w-full min-w-0">
      <div className="flex flex-col py-2 sm:py-3 px-2 sm:px-4 justify-center items-center gap-2 self-stretch rounded-2xl border-[0.5px] border-primary-300 bg-grayscale-100 shadow-custom-light w-full min-w-0">
        { attachedFiles.length > 0 && (
          <div className="flex items-start gap-2 sm:gap-3 flex-wrap self-stretch p-2 w-full">
            {attachedFiles.map((fileWrapper) => (
              <AttachmentPreview key={fileWrapper.id} item={fileWrapper} onRemove={() => handleRemoveFile(fileWrapper.id)} />
            ))}
          </div>
        )}
        <div className="flex w-full items-center gap-2 sm:gap-4 min-w-0">
            <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onFocus={() => trackClick('cosmos_focus_input')}
                onKeyPress={handleKeyPress}
                placeholder={hasMessages ? "" : "What is your R&D problem statement?"}
                className="flex-1 bg-transparent text-grayscale-1100 font-grotesk text-xs sm:text-sm focus:outline-none resize-none hide-scrollbar max-h-[120px] overflow-y-auto min-w-0"
                rows={1}
                // disabled={isLoading}
            />
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                {!disableAttachments && (
                    <>
                        <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} multiple accept="image/*,application/pdf" />
                        <button className="p-2 sm:p-3 rounded-full hover:bg-grayscale-200 flex-shrink-0" disabled={isLoading} onClick={handleAttachmentClick}>
                            <AttachmentIcon />
                        </button>
                    </>
                )}
                <button
                    onClick={handleSend}
                    className="p-2 sm:p-3 rounded-full bg-grayscale-1200 hover:bg-grayscale-1100 disabled:bg-grayscale-600 flex-shrink-0"
                    disabled={isLoading || !inputValue.trim()}
                >
                    <UploadIcon />
                </button>
            </div>
        </div>
      </div>
      {showConciergeLink && (
        <div className="flex items-center gap-3">
          <p className="text-center font-grotesk text-xs">
            <span className="text-grayscale-1300">For further assistance, </span>
            <button onClick={() => {
              trackClick('cosmos_click_book_call');
              // Logged on the click, so someone who opens the form and never
              // submits it still reaches the interest table.
              recordInterest('book_call');
              openModal(ModalType.BookCall);
            }} className="font-bold text-primary-og-600 underline">book a free call</button>
            <span className="text-grayscale-1300"> with our concierge</span>
          </p>
        </div>
      )}
    </div>
  );
};